import React, { useEffect, useRef, useState } from "react";
import "./Tour.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  setSteps,
  startTour,
  stopTour,
  updateIsNewOrg,
  updateNextTourIndex,
  updatePreviousTourIndex,
} from "../../redux/features/tour-slice";
import { useLocation, useNavigate } from "react-router-dom";

import { useClickOutside } from "use-events";
import ROUTES from "../../constants/routes";
import {
  updateTourCheckout,
  updateTourProduct,
  updateTourSales,
} from "../../redux/features/user-slice";
import {
  updateTourCheckoutStatus,
  updateTourProductStatus,
  updateTourSalesStatus,
} from "../../api/userManagement";
import { useViewPortDetector } from "../../helpers/viewPortDetector";

const Tour = () => {
  const { currentViewPort } = useViewPortDetector();
  const tourRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { steps, run, activeIndex, is_new_org } = useSelector(
    (state) => state.tourReducer.value
  );
  const {
    id: user_id,
    open_onboard,
    tour_product,
    tour_checkout,
    tour_sales,
  } = useSelector((state) => state.userReducer.value);
  const { id: organization_id, loading: loading_org } = useSelector(
    (state) => state.organizationReducer.value
  );

  const { products } = useSelector((state) => state.productReducer.value);
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState({});
  const [containerStyle, setContainerStyle] = useState({});
  const [currentTour, setCurrentTour] = useState(null);

  useEffect(() => {
    const tour_steps = [];

    if (
      user_id &&
      !loading_org &&
      (!organization_id || tour_product || tour_checkout || tour_sales)
    ) {
      if (!organization_id) {
        setCurrentTour(null);
        if (location.pathname === ROUTES.home.path) {
          tour_steps.push({
            title: "Welcome to DAIKOFIN",
            intro:
              "Your all-in-one app to manage products, track, sales, and grow your business. Let's take a quick tour!",
            path: ROUTES.home.path,
          });
          if (currentViewPort === "mobile") {
            tour_steps.push({
              title: "Menu",
              intro:
                "This is your Main Menu - the heart of DAIKOFIN. From here, you can manage products, check sales, access reports, and customize settings.",
              selector: "#navbar-bars",
              path: ROUTES.home.path,
            });
          }
          tour_steps.push({
            title: "Your Organization",
            intro:
              'Tap the "My Organization" tab to manage branches and edit your organization details. But firstly, you have to create your organization.',
            selector: "#navbar-organization",
            navbar: true,
          });
          tour_steps.push({
            title: "Add Organization",
            intro: "Let's create your organization.",
            path: ROUTES.addOrganization.path,
          });
          tour_steps.push({
            title: "Create Organization",
            intro:
              "Fill this form and click this button to create your organization and get you started.",
            selector: "#tour-add-organization",
          });
        }
      } else if (organization_id && is_new_org) {
        setCurrentTour("organization");
        tour_steps.push({
          title: "Organization",
          intro: "Here you can view and manage your organization.",
          path: ROUTES.myOrganization.path,
        });
        tour_steps.push({
          title: "Edit Organization",
          intro: "Click this button to edit your organization details.",
          selector: "#tour-edit-organization",
        });
        if (currentViewPort === "mobile") {
          tour_steps.push({
            title: "Add new branches",
            intro:
              "Click this button to add new branches. You can edit by clicking pen icon and delete the branch by clicking trash icon for respective branches",
            selector: "#tour-add-branch",
          });
        } else {
          tour_steps.push({
            title: "Add new branches",
            intro:
              "Fill this form and submit to create new branch. You can edit by clicking pen icon and delete the branch by clicking trash icon for respective branches",
            selector: "#tour-add-branch-desktop",
          });
        }
      } else if (
        tour_product &&
        location.pathname === ROUTES.manageProduct.path
      ) {
        setCurrentTour("product");
        tour_steps.push({
          title: "Products",
          intro:
            'Tap the "PRODUCTS" tab to manage your all products or to add new product.',
          selector: "#navbar-product",
          navbar: true,
        });
        tour_steps.push({
          title: "Add Products",
          intro:
            "Let's add your first product. Go to the 'Products' section and tap the '+' button to add a new item.",
          selector:
            currentViewPort === "mobile"
              ? "#tour-add-product"
              : "#tour-add-product-desktop",
          path: ROUTES.manageProduct.path,
        });
        tour_steps.push({
          title: "Product Details",
          intro:
            "In this tab, enter the required informations about your product.",
          selector: "#tour-product-tag",
          path: currentViewPort === "mobile" ? ROUTES.addProduct.path : null,
        });
        tour_steps.push({
          title: "ADD/EDIT/NEXT Button",
          intro:
            "Click this button to create your product. When your are editing your product this button will be used as edit product. And when your organization is subscribed to our PRO/PLUS plan, this button will be used for changing the tab to stock, for your stock management.",
          selector: "#tour-add-product-button",
        });
        tour_steps.push({
          title: "Delete Button",
          intro:
            "You will see this button only when you are editing your product and and can be used for deleting the product.",
          selector: "#tour-delete-product-button",
        });
        tour_steps.push({
          title: "Stock Tab",
          intro:
            "When you are subscribed to our PRO/PLUS plan, you will be able to use this tab for managing your product's stocks.",
          selector: "#tour-stock-tag",
        });
      } else if (tour_checkout && location.pathname === ROUTES.checkout.path) {
        setCurrentTour("checkout");
        if (products?.length > 0) {
          tour_steps.push({
            title: "Checkout",
            intro:
              'Tap the "Checkout" tab to begin with your first checkout sale.',
            selector: "#navbar-checkout",
            navbar: true,
          });
          tour_steps.push({
            title: "Checkout Section",
            intro:
              "Welcome to the checkout section! Here's where you will see your products, and complete purchases seamlessly.",
            path: ROUTES.checkout.path,
          });
          if (currentViewPort === "mobile") {
            tour_steps.push({
              title: "Add Product to Cart",
              intro:
                "Select single or multiple product and then tap on checkout. Here, selecting same items multiple time will increase the product's sale quantity.",
              selector: "#tour-checkout",
              path: ROUTES.checkout.path,
            });
          } else {
            tour_steps.push({
              title: "Add Product to Cart",
              intro:
                "Select single or multiple product and selected products will be listed in this cart. Here, selecting same items multiple time will increase the product's sale quantity.",
              selector: "#tour-checkout-desktop",
              path: ROUTES.checkout.path,
            });
          }
          tour_steps.push({
            title: "Add Discounts",
            intro: "Tap on 'add Discount' for a seamless price.",
            selector: "#tour-cart-discount",
            path: currentViewPort === "mobile" ? ROUTES.cart.path : null,
          });
          tour_steps.push({
            title: "More Options",
            intro:
              "Next, tap on a more option if you want to add discount/ note/ delivery fee/ clearing cart as well.",
            selector: "#tour-more-options",
          });
          tour_steps.push({
            title: "Move To Payment",
            intro: "Tap this button to make the payment.",
            selector: "#tour-next-to-pay",
            path:
              currentViewPort === "mobile"
                ? ROUTES.cart.path
                : ROUTES.checkout.path,
          });
          tour_steps.push({
            title: "Payment Details",
            intro:
              "Now, it's time to add your payment details. Select from various methods, like credit card, digital wallets, or cash on delivery.",
            selector: "#tour-payment-details",
            path: ROUTES.payment.path,
          });
          tour_steps.push({
            title: "Complete Sale",
            intro:
              "After securing all the amount from the payment methods(single/multiple), click this button to create your sale",
            selector: "#tour-payment-button",
            path: ROUTES.payment.path,
          });
        } else {
          setCurrentTour("createProduct");
          tour_steps.push({
            title: "Create Product",
            intro: "Before making your 1st sale, you have to add a product.",
            path: ROUTES.checkout.path,
          });
        }
      } else if (tour_sales && location.pathname === ROUTES.sales.path) {
        setCurrentTour("sales");
        tour_steps.push({
          title: "Sales",
          intro:
            "To see your sales details, navigate to the 'Sales' section. Here, you'll find a list of all transactions along with their details.",
          selector: "#navbar-sales",
          navbar: true,
        });
        tour_steps.push({
          title: "Filter",
          intro:
            "Looking for a specific transaction? Use the search bar at the top or apply filters like date, product, or payment type to refine your results.",
          path: ROUTES.sales.path,
          selector:
            currentViewPort === "mobile"
              ? "#tour-sales-filter"
              : "#tour-sales-filter-desktop",
        });
        tour_steps.push({
          title: "Sale Changes",
          intro:
            "Need to make changes to a sale? Tap the 'Return/Refund' icon to return the product.",
          selector: "#tour-return-button",
          path: currentViewPort === "mobile" ? ROUTES.salesDetails.path : null,
        });
        tour_steps.push({
          title: "Sale Changes",
          intro:
            "Or tap the 'cancel transactions' to remove the transaction.",
          selector: "#tour-cancel-button",
          path: currentViewPort === "mobile" ? ROUTES.salesDetails.path : null,
        });
        tour_steps.push({
          title: "Export reports",
          intro:
            "Want to generate reports? Tap the 'Export' button to download detailed reports for your sales records. Choose from options like daily, weekly, or monthly summaries.",
          selector: "#tour-sales-export",
          path: ROUTES.sales.path,
        });
      }
    }
    if (!run || currentTour === "createProduct") {
      dispatch(setSteps({ steps: tour_steps }));
    }
  }, [
    organization_id,
    tour_product,
    tour_checkout,
    tour_sales,
    is_new_org,
    loading_org,
    user_id,
    currentViewPort,
    products,
    location.pathname,
  ]);

  useEffect(() => {
    if (steps?.length > 0 && user_id) {
      dispatch(startTour());
    } else {
      dispatch(stopTour());
    }
  }, [steps]);

  useEffect(() => {
    const previousTarget = document.querySelector(".tour-highlight");
    if (previousTarget) {
      previousTarget.remove();
    }
  }, [activeIndex, run, activeStep]);

  const finishTour = () => {
    dispatch(stopTour());
    dispatch(setSteps({ steps: [] }));
    setCurrentTour(null);
    if (currentTour === "product") {
      dispatch(updateTourProduct({ tour_product: false }));
      updateTourProductStatus(user_id)
        .then(() => {})
        .catch(() => {});
    } else if (currentTour === "checkout") {
      dispatch(updateTourCheckout({ tour_checkout: false }));
      updateTourCheckoutStatus(user_id)
        .then(() => {})
        .catch(() => {});
      navigate(ROUTES.checkout.path);
    } else if (currentTour === "sales") {
      dispatch(updateTourSales({ tour_sales: false }));
      updateTourSalesStatus(user_id)
        .then(() => {})
        .catch(() => {});
      navigate(ROUTES.sales.path);
    } else if (currentTour === "organization") {
      dispatch(updateIsNewOrg(false));
    } else if (currentTour === "createProduct") {
      navigate(ROUTES.manageProduct.path);
    }
  };

  useEffect(() => {
    if (run && !open_onboard) {
      if (steps[activeIndex]) {
        if (steps[activeIndex]?.path) {
          navigate(steps[activeIndex].path);
        }
        setActiveStep(steps[activeIndex]);
      } else {
        setContainerStyle({
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        });
      }
    }
  }, [steps, activeIndex, run, location.pathname, open_onboard]);

  function copyStylesRecursively(sourceElement, clonedElement) {
    const computedStyle = window.getComputedStyle(sourceElement);
    for (let i = 0; i < computedStyle.length; i++) {
      const styleProp = computedStyle[i];
      clonedElement.style[styleProp] =
        computedStyle.getPropertyValue(styleProp);
    }

    Array.from(sourceElement.children).forEach((child, index) => {
      if (clonedElement.children[index]) {
        copyStylesRecursively(child, clonedElement.children[index]);
      }
    });
  }

  useEffect(() => {
    const targetSelector = activeStep?.selector;

    if (targetSelector) {
      const targetElement = document.querySelector(targetSelector);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const { top, bottom, left, width, height } = rect;

        const clonedElement = targetElement.cloneNode(true);
        copyStylesRecursively(targetElement, clonedElement);

        clonedElement.style.position = "fixed";
        clonedElement.style.height = `${height}px`;
        clonedElement.style.width = `${width}px`;

        // Position cloned element
        let clonedTop, clonedLeft;

        if (bottom > window.innerHeight) {
          clonedTop = window.innerHeight - height - 20;
        } else {
          clonedTop = top;
        }

        if (left < 0) {
          clonedLeft = 0;
        } else {
          clonedLeft = left;
        }

        clonedElement.style.top = `${clonedTop}px`;
        clonedElement.style.left = `${clonedLeft}px`;
        clonedElement.style.zIndex = "3000";
        clonedElement.style.boxShadow = "0 0 10px rgba(0, 0, 0, 0.5)";
        clonedElement.style.cursor = "pointer";

        clonedElement.classList.add("tour-highlight");
        clonedElement.onclick = () => {
          handleNext();
        };
        document.body.appendChild(clonedElement);

        // Get container dimensions
        const containerHeight =
          tourRef && tourRef.current ? tourRef.current.offsetHeight : 200; // fallback height
        const containerWidth =
          tourRef && tourRef.current ? tourRef.current.offsetWidth : 300; // fallback width

        // Calculate spacing between elements
        const spacing = 20;

        // Determine best position for container relative to cloned element
        let containerTop, containerLeft;

        // Try to position below cloned element first
        if (
          clonedTop + height + spacing + containerHeight <=
          window.innerHeight
        ) {
          containerTop = clonedTop + height + spacing;
          containerLeft = Math.max(
            0,
            Math.min(
              clonedLeft + width / 2 - containerWidth / 2,
              window.innerWidth - containerWidth
            )
          );
        }
        // Try to position above cloned element
        else if (clonedTop - spacing - containerHeight >= 0) {
          containerTop = clonedTop - spacing - containerHeight;
          containerLeft = Math.max(
            0,
            Math.min(
              clonedLeft + width / 2 - containerWidth / 2,
              window.innerWidth - containerWidth
            )
          );
        }
        // Try to position to the right
        else if (
          clonedLeft + width + spacing + containerWidth <=
          window.innerWidth
        ) {
          containerTop = Math.max(
            0,
            Math.min(
              clonedTop + height / 2 - containerHeight / 2,
              window.innerHeight - containerHeight
            )
          );
          containerLeft = clonedLeft + width + spacing;
        }
        // Try to position to the left
        else if (clonedLeft - spacing - containerWidth >= 0) {
          containerTop = Math.max(
            0,
            Math.min(
              clonedTop + height / 2 - containerHeight / 2,
              window.innerHeight - containerHeight
            )
          );
          containerLeft = clonedLeft - spacing - containerWidth;
        }
        // Fallback: position at bottom of screen
        else {
          containerTop = window.innerHeight - containerHeight - 20;
          containerLeft = Math.max(
            0,
            Math.min(
              window.innerWidth / 2 - containerWidth / 2,
              window.innerWidth - containerWidth
            )
          );
        }

        setContainerStyle({
          top: `${containerTop}px`,
          left: `${containerLeft}px`,
          transform: "none", // Remove transform since we're using absolute positioning
        });
      } else {
        // Fallback when target element is not found
        setContainerStyle({
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        });
      }
    } else {
      // Fallback when no selector
      setContainerStyle({
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      });
    }
  }, [activeStep]);

  const handleBack = () => {
    if (activeStep?.path) {
      navigate(-1);
    }
    dispatch(updatePreviousTourIndex());
  };

  const handleNext = () => {
    if (activeIndex < steps?.length - 1) {
      dispatch(updateNextTourIndex());
    } else {
      finishTour();
    }
  };

  useClickOutside([tourRef], () => {
    if (run && !open_onboard) {
      handleNext();
    }
  });

  return (
    <>
      <div
        className={`tour-container ${
          run && !open_onboard && activeIndex !== null ? "active" : ""
        }`}
        style={containerStyle}
        ref={tourRef}
      >
        <div className="contents">
          <p className="skip" onClick={finishTour}>
            Skip
          </p>
          <p className="title">{activeStep?.title}</p>
          <p className="intro">{activeStep?.intro}</p>
          <div className="last">
            <button type="button" disabled={!activeIndex} onClick={handleBack}>
              Back
            </button>
            <div className="dots">
              {steps?.map((el, i) => {
                return (
                  <span
                    key={i}
                    className={activeIndex === i ? "active" : ""}
                  ></span>
                );
              })}
            </div>
            <button
              type="button"
              disabled={activeIndex === null}
              onClick={handleNext}
            >
              {activeIndex === steps?.length - 1 ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`tour-back-blur ${
          run && !open_onboard && activeIndex !== null ? "active" : ""
        }`}
      ></div>
    </>
  );
};

export default Tour;
